import { Alert, Button, Flex, Group, Modal, Text, Title } from '@mantine/core';
import { ConfirmModal } from '../../components/confirm-modal';
import { useEffect, useRef, useState } from 'react';

import { IconAlertCircle, IconArrowLeft } from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useParams } from 'react-router-dom';
import { CustomLoader } from '../../components/custom-loader';
import { rootGoBack } from '../../components/custom-router';
import { Page } from '../../components/page';
import {
  useCalculateBankHours,
  useCheckBankHoursToUse,
  useEntryBankHours,
  useGetBankHoursHistory,
  usePayBankHour,
  useShowBankHours,
  useToUseBankHour,
} from '../../data/hooks/bank-hours';
import { useGetEmployee } from '../../data/hooks/employee';
import { useGetWorkScale } from '../../data/hooks/work-scale';
import { showUserRequest } from '../../data/user';
import { User } from '../../models/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { PaidBankHour } from '../../@types/bank-hour';
import { PaymentReceiptModal } from '../../components/payment-receipt-modal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { formatHours, parsePayHours } from '../../utils/helpers';
import { BankInfo } from './components/bank-info';
import { PayBankForm } from './forms/pay-bank-form';
import { UseBankForm } from './forms/use-bank-form';
import { BankHourHistoryType } from '../../models/bank-hour';
import { BankHourHistoryTypeHumanized } from '../../utils/constants/bank-hour';
import TotalHoursInput, {
  HOURS_REGEX,
} from '../../components/custom-hours-input';
import { useForm } from '@mantine/form';

type PageModalState = 'pay-bank' | 'use-bank' | 'add-hours' | null;
type ConfirmState = 'pay' | 'use' | null;
const CALENDAR_INITIAL_STATE: [Date | null, Date | null] = [null, null];

export function BankHoursHistory() {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const [paidReceipt, setPaidReceipt] = useState<{
    payments: PaidBankHour[];
    period: string;
  } | null>(null);

  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const page = useRef(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<User>();
  const [selectedDateToUse, setSelectedDateToUse] = useState<Date[]>([]);
  const [selectedDateToPay, setSelectedDateToPay] = useState<
    [Date | null, Date | null]
  >(CALENDAR_INITIAL_STATE);
  const [payHours, setPayHours] = useState('');

  const formEntryHours = useForm({
    initialValues: {
      entryHours: '',
    },
    validate: {
      entryHours: (value) =>
        !HOURS_REGEX.test(value)
          ? 'Use HH:MM (ex: 142:18 ou -142:18)'
          : /^-?0+:00$/.test(value)
          ? 'Informe um valor diferente de zero'
          : null,
    },
  });

  const {
    fetch: getBankHourHistoryFetcher,
    response: getBankHourHistoryResponse,
    loading: getBankHourHistoryLoader,
  } = useGetBankHoursHistory();

  const {
    fetch: showBankHoursFetcher,
    response: showBankHoursResponse,
    loading: showBankHoursLoader,
  } = useShowBankHours();

  const { fetch: getEmployeeFetcher, loading: getEmployeeLoader } =
    useGetEmployee();

  const {
    fetch: getWorkScaleFetcher,
    response: getrWorkScaleResponse,
    loading: getWorkScaleLoader,
  } = useGetWorkScale();

  const {
    fetch: calculateBankHoursFetcher,
    loading: calculateBankHoursLoader,
    response: calculateBankHoursResponse,
  } = useCalculateBankHours();

  const { fetch: payBankHourFetcher, loading: payBankHourLoader } =
    usePayBankHour();

  const { fetch: useBankHourFetcher, loading: useBankHourLoader } =
    useToUseBankHour();

  const {
    fetch: checkBankHoursToUseFetcher,
    loading: checkBankHoursToUseLoader,
    response: checkBankHoursToUseResponse,
  } = useCheckBankHoursToUse();

  const { fetch: entryHoursFetcher, loading: entryHoursLoader } =
    useEntryBankHours();

  async function handlePageChange(newPage: number) {
    page.current = newPage;
    handleGetBankHourHistory();
  }

  function handleCloseModal() {
    setSelectedDateToUse([]);
    setSelectedDateToPay([null, null]);
    setPayHours('');
    setConfirm(null);
    setPageModal(null);
    formEntryHours.reset();
  }

  async function handleGetBankHourHistory() {
    await getBankHourHistoryFetcher({
      params: {
        limit: 10,
        page: page.current,
        bankHour: Number(id),
      },
    });
  }

  /** balance + history, after anything that moves the balance */
  async function refreshBank() {
    page.current = 1;
    await Promise.all([
      handleGetBankHourHistory(),
      showBankHoursFetcher({ params: { id: Number(id) } }),
    ]);
  }

  async function handleShowBankHour() {
    await showBankHoursFetcher({
      params: {
        id: Number(id),
      },
      onSuccess: async (res) => {
        await getUserInfo(res.user);
      },
    });
  }

  async function getUserInfo(userId: number) {
    try {
      setPageLoading(true);
      const response = await showUserRequest(Number(userId));
      await getEmployeeFetcher({
        id: Number(userId),
        onSuccess: (res) => {
          getWorkScaleFetcher({
            id: Number(res.workScale),
          });
        },
      });
      setUserInfo(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuário.',
        message: 'tente novamente.',
      });
    }
  }

  async function handlePayBankHour() {
    setConfirm(null);
    const formatedDates = selectedDateToPay
      .map((item) => item?.toISOString())
      .filter((date): date is string => date !== undefined);

    if (!formatedDates[1]) {
      errorNotification({
        title: 'Selecione um intervalo de datas',
        message: 'Tente novamente',
      });
      return;
    }

    const period = `${formatLocale(
      formatedDates[0],
      'DD/MM/YY',
    )} a ${formatLocale(formatedDates[formatedDates.length - 1], 'DD/MM/YY')}`;

    const paySeconds = parsePayHours(payHours);
    const total = calculateBankHoursResponse?.[0]?.totalTime ?? 0;

    if (
      paySeconds !== undefined &&
      (Number.isNaN(paySeconds) || paySeconds <= 0 || paySeconds > total)
    ) {
      errorNotification({
        title: 'Horas a pagar inválidas',
        message: 'Informe HH:MM maior que zero e até o total do período.',
      });
      return;
    }

    await payBankHourFetcher({
      data: {
        users: [Number(userInfo?.id)],
        interval: formatedDates,
        // below the total = partial payment; the rest stays in the bank
        ...(paySeconds !== undefined && paySeconds < total
          ? { seconds: paySeconds }
          : {}),
      },
      onSuccess: (paid) => {
        successNotification({
          title: 'Banco de horas pago com successo!',
          message: 'Tudo certo',
        });
        setSelectedDateToPay(CALENDAR_INITIAL_STATE);
        setPayHours('');
        setConfirm(null);
        setPageModal(null);
        refreshBank();
        setPaidReceipt({ payments: paid, period });
      },
    });
  }

  async function handleUseBankHour() {
    setConfirm(null);
    const formatedDates = selectedDateToUse.map((item) => item?.toISOString());

    if (!formatedDates.length) {
      errorNotification({
        title: 'Selecione um intervalo de datas',
        message: 'Tente novamente',
      });
      return;
    }

    await useBankHourFetcher({
      data: {
        users: [Number(userInfo?.id)],
        interval: formatedDates,
      },
      onSuccess: () => {
        successNotification({
          title: 'Banco de horas concedido com successo!',
          message: 'Tudo certo',
        });
        setSelectedDateToUse([]);
        setConfirm(null);
        setPageModal(null);
        refreshBank();
      },
    });
  }

  async function handleEntryHours({ ...values }: typeof formEntryHours.values) {
    const [hours, minutes] = values.entryHours.split(':').map(Number);

    const isNegative = values.entryHours.startsWith('-');
    const seconds =
      (Math.abs(hours) * 3600 + minutes * 60) * (isNegative ? -1 : 1);

    await entryHoursFetcher({
      bankId: Number(id),
      data: {
        entryHours: seconds,
      },
      onSuccess: () => {
        refreshBank();
        handleCloseModal();
      },
    });
  }

  useEffect(() => {
    handleGetBankHourHistory();
    handleShowBankHour();
  }, []);

  useEffect(() => {
    if (selectedDateToPay[1]) {
      const fetchData = async () => {
        await calculateBankHoursFetcher({
          params: {
            users: Number(userInfo?.id).toString(),
            interval: selectedDateToPay
              .map((item) => item?.toISOString())
              .join(','),
          },
        });
      };
      fetchData();
    }

    if (selectedDateToUse.length) {
      const fetchData = async () => {
        await checkBankHoursToUseFetcher({
          params: {
            users: Number(userInfo?.id).toString(),
            interval: selectedDateToUse
              .map((item) => item?.toISOString())
              .join(','),
          },
        });
      };
      fetchData();
    }
  }, [selectedDateToPay, selectedDateToUse.length]);

  return (
    <Page>
      <CustomLoader
        loading={
          calculateBankHoursLoader ||
          checkBankHoursToUseLoader ||
          showBankHoursLoader ||
          getEmployeeLoader ||
          pageLoading ||
          getWorkScaleLoader
        }
      />
      <Group position="left">
        <Button
          leftIcon={<IconArrowLeft />}
          onClick={rootGoBack}
          mt={16}
          mb={16}
          variant="subtle"
        >
          voltar
        </Button>
      </Group>

      {userInfo && (
        <BankInfo
          userInfo={userInfo}
          workScale={getrWorkScaleResponse}
          setPageModal={setPageModal}
          amountBankHour={showBankHoursResponse?.composedAmount ?? ''}
          dailySeconds={showBankHoursResponse?.dailyWorkAmount}
          manualSeconds={showBankHoursResponse?.amount}
        />
      )}
      <Flex m={16} direction="column">
        <Title order={2}>Histórico de banco de horas</Title>

        <DataTable
          recordsPerPage={10}
          onPageChange={handlePageChange}
          totalRecords={getBankHourHistoryResponse?.meta.totalItems}
          page={page.current}
          fetching={getBankHourHistoryLoader}
          records={getBankHourHistoryResponse?.items ?? []}
          noRecordsText="Sem registros"
          withBorder={false}
          borderRadius="sm"
          height="70vh"
          highlightOnHover
          verticalSpacing={'md'}
          fontSize={'sm'}
          columns={[
            {
              accessor: 'type',
              title: 'Tipo',
              width: 160,
              render: ({ type, amount }) => {
                const effectiveType =
                  amount < 0
                    ? BankHourHistoryType.EXIT
                    : amount > 0
                    ? BankHourHistoryType.ENTRY
                    : type;
                return (
                  BankHourHistoryTypeHumanized[effectiveType] ??
                  BankHourHistoryTypeHumanized[type]
                );
              },
            },
            {
              accessor: 'date',
              title: 'Data',
              width: 250,
              render: ({ createdAt }) => {
                return formatLocale(createdAt, 'DD/MM/YY');
              },
            },
            {
              accessor: 'amount',
              title: 'Horas',
              width: 250,
              render: ({ amount }) => {
                const totalMinutes = Math.round(Math.abs(amount) / 60);
                if (totalMinutes === 0) {
                  return (
                    <Text fw="bold" color="dimmed">
                      00h 00min
                    </Text>
                  );
                }

                const isPositive = amount > 0;
                return (
                  <Text fw="bold" color={isPositive ? 'teal.5' : 'red'}>
                    {isPositive ? '+' : '-'}
                    {formatHours(totalMinutes)}
                  </Text>
                );
              },
            },
            {
              accessor: 'description',
              title: 'Detalhamento',
              render: ({ description, amount }) => {
                if (description === 'Entrada de horas manual' && amount < 0) {
                  return 'Saída de horas manual';
                }
                return description;
              },
            },
          ]}
        />
      </Flex>
      <Modal
        fullScreen={isMobile}
        title="Usar banco de horas"
        opened={pageModal === 'use-bank'}
        onClose={() => {
          handleCloseModal();
        }}
        size={460}
      >
        <UseBankForm
          useBankHourTotalHours={
            checkBankHoursToUseResponse?.[0]?.totalAvailableTime ?? ''
          }
          requestedHours={
            checkBankHoursToUseResponse?.[0]?.totalResquestedTime ?? ''
          }
          selectedDates={selectedDateToUse}
          setSelectedDates={setSelectedDateToUse}
          loading={useBankHourLoader}
          handleUseBankHour={() => setConfirm('use')}
          userInfo={userInfo}
          canUserHours={checkBankHoursToUseResponse?.[0]?.isAllowedToUse}
        />
      </Modal>
      <Modal
        fullScreen={isMobile}
        title="Pagar banco de horas"
        opened={pageModal === 'pay-bank'}
        onClose={() => {
          handleCloseModal();
        }}
        size={460}
      >
        <PayBankForm
          selectedDates={selectedDateToPay}
          setSelectedDates={setSelectedDateToPay}
          loading={payBankHourLoader}
          handlePayBankHour={() => setConfirm('pay')}
          calculatedBankHours={calculateBankHoursResponse ?? []}
          userInfo={userInfo}
          payHours={payHours}
          setPayHours={setPayHours}
        />
      </Modal>
      <PaymentReceiptModal
        payments={paidReceipt?.payments ?? null}
        period={paidReceipt?.period ?? ''}
        onClose={() => setPaidReceipt(null)}
      />
      <ConfirmModal
        opened={confirm === 'pay'}
        title="Confirmar pagamento"
        confirmLabel="Pagar horas"
        loading={payBankHourLoader}
        onCancel={() => setConfirm(null)}
        onConfirm={handlePayBankHour}
      >
        <Text>
          Pagar{' '}
          <b>
            {payHours.trim()
              ? payHours.trim()
              : calculateBankHoursResponse?.[0]?.totalTimeFormated}
          </b>{' '}
          de <b>{userInfo?.username}</b>? As horas pagas saem do saldo e o
          período não poderá ser pago de novo.
          {payHours.trim() &&
            ' O restante continua no saldo e poderá ser pago depois.'}
        </Text>
      </ConfirmModal>
      <ConfirmModal
        opened={confirm === 'use'}
        title="Confirmar folga"
        confirmLabel="Usar horas"
        loading={useBankHourLoader}
        onCancel={() => setConfirm(null)}
        onConfirm={handleUseBankHour}
      >
        <Text>
          Conceder{' '}
          <b>{checkBankHoursToUseResponse?.[0]?.totalResquestedTime}</b> de
          folga para <b>{userInfo?.username}</b> em {selectedDateToUse.length}{' '}
          dia(s)? Os dias serão justificados como banco de horas.
        </Text>
      </ConfirmModal>
      <Modal
        fullScreen={isMobile}
        title="Adicionar horas"
        opened={pageModal === 'add-hours'}
        onClose={() => {
          handleCloseModal();
        }}
        size={560}
      >
        <Flex direction={'column'} gap={'md'}>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Atenção"
            color="blue"
          >
            <Text> Digite o tempo total no formato HHH:MM, onde:</Text>
            <Text mt="sm">
              ● HHH representa o número total de horas (pode ser maior que 24).
            </Text>
            <Text mb={'sm'}>● MM representa os minutos (de 00 a 59).</Text>
            <Text>
              <b>Exemplo válido:</b> 142:18 → 142 horas e 18 minutos.
            </Text>
          </Alert>
          <form
            onSubmit={formEntryHours.onSubmit((values) =>
              handleEntryHours(values),
            )}
          >
            <Flex direction={'column'} gap={'md'}>
              <TotalHoursInput formRef={formEntryHours} />
              <Button
                color="green"
                fullWidth
                type="submit"
                loading={entryHoursLoader}
              >
                Adicionar
              </Button>{' '}
            </Flex>
          </form>
        </Flex>
      </Modal>
    </Page>
  );
}
